import { useEffect, useMemo, useRef, useState } from "react";
import { Avatar, type AvatarUser } from "@/components/ui/Avatar";
import { Icon } from "@/components/ui/Icon";
import { Popover } from "@/components/ui/Popover";
import { useBoardPeople } from "@/hooks/useBoardPeople";
import { useUserSearch } from "@/hooks/useUserSearch";
import { useWorkspaceMembers } from "@/hooks/useWorkspaceMembers";
import { deriveAvatarColor, deriveInitials } from "@/lib/colors";
import { BRAND } from "@/lib/tokens";
import { t } from "@/lib/strings";
import type { UUID, UserSearchResult } from "@/lib/types";

interface Props {
  anchor: HTMLElement | null;
  value: UUID[];
  allowMultiple: boolean;
  /** When set, the picker pre-loads workspace members + flags
   * yet-to-be-added users with an invite hint. */
  workspaceId?: UUID;
  /** When set, the default pool is the board's people (members + guests), so
   * already-assigned guests appear and can be toggled off. */
  boardId?: UUID;
  /** Hide these user ids from search results (e.g. exclude the actor). */
  excludeUserIds?: UUID[];
  /** Hint shown for a non-member result. Defaults to the board-assignment text
   * ("Erhält Gast-Zugriff"); the member-management UI overrides it. */
  inviteLabel?: string;
  onClose: () => void;
  onChange: (next: UUID[]) => void;
}

function toAvatarUser(u: {
  id: string;
  display_name: string | null;
  email: string;
  username: string | null;
  avatar_url: string | null;
  department_workspace_id?: string | null;
  department_color?: string | null;
}): AvatarUser {
  const name = u.display_name ?? u.email;
  return {
    id: u.id,
    display_name: name,
    initials: deriveInitials(name),
    avatar_color: deriveAvatarColor(u.id),
    username: u.username,
    department_workspace_id: u.department_workspace_id ?? null,
    department_color: u.department_color ?? null,
  };
}

/**
 * The picker in a floating Popover — the normal case (a person CELL, member management).
 *
 * 🔴 Do NOT use this inside another floating surface. A Popover anchored to a button that lives
 * inside a menu drops its list ON TOP of that menu's own controls. That is exactly how the bulk
 * "Feld setzen -> Verantwortlich" flow broke: the list covered the Übernehmen button, so a
 * colleague could pick people and then had nothing to press — and whether the button was
 * reachable depended on where the popover happened to flip, which is why it felt flaky. Use
 * `PersonSearchList` there and render it INLINE. The share dialog learned the same lesson when a
 * portaled picker inside a modal ate its keystrokes.
 */
export function PersonPicker({ anchor, onClose, ...rest }: Props) {
  return (
    <Popover anchor={anchor} onClose={onClose} width={320} align="left">
      <PersonSearchList {...rest} onClose={onClose} />
    </Popover>
  );
}

/**
 * Search + list with NO floating container of its own. Renders in normal flow, so a caller can
 * put it inside a menu and keep its own buttons reachable BELOW it.
 */
export function PersonSearchList({
  value,
  allowMultiple,
  workspaceId,
  boardId,
  excludeUserIds,
  inviteLabel,
  onClose,
  onChange,
}: Omit<Props, "anchor">) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(q), 180);
    return () => window.clearTimeout(id);
  }, [q]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const search = useUserSearch(debounced, debounced.length >= 1);
  // Default pool: the board's people (members + already-assigned guests) when a
  // boardId is given — so a guest you assigned shows here and can be un-clicked.
  // Falls back to workspace members (e.g. the member-management picker).
  const boardPeople = useBoardPeople(boardId);
  const members = useWorkspaceMembers(boardId ? undefined : workspaceId);
  const directory = useMemo(() => {
    const src = boardId ? (boardPeople.data ?? []) : (members.data ?? []);
    return src.map((d) => ({
      user_id: d.user_id,
      email: d.email,
      display_name: d.display_name,
      username: d.username,
      avatar_url: d.avatar_url,
      department_workspace_id: d.department_workspace_id ?? null,
      department_color: d.department_color ?? null,
    }));
  }, [boardId, boardPeople.data, members.data]);
  // "Known" people (won't get the guest-access hint on a search result).
  const knownIds = useMemo(() => new Set(directory.map((d) => d.user_id)), [directory]);
  const exclude = useMemo(() => new Set(excludeUserIds ?? []), [excludeUserIds]);

  // No query → show the directory as the default pool. Query → global search.
  const items: UserSearchResult[] =
    debounced.length === 0
      ? directory
          .filter((m) => !exclude.has(m.user_id))
          .map((m) => ({
            id: m.user_id,
            email: m.email,
            display_name: m.display_name,
            username: m.username,
            avatar_url: m.avatar_url,
            keycloak_sub: "",
            department_workspace_id: m.department_workspace_id ?? null,
            department_color: m.department_color ?? null,
          }))
      : (search.data ?? []).filter((u) => !exclude.has(u.id));

  function toggle(id: UUID) {
    if (allowMultiple) {
      onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
    } else {
      onChange([id]);
      onClose();
    }
  }

  const valueSet = new Set(value);

  return (
    <>
      <div style={{ padding: 10, borderBottom: "1px solid var(--hairline-soft)" }}>
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t.assignSearchPlaceholder}
          style={{
            width: "100%",
            height: 32,
            padding: "0 10px",
            fontSize: 13,
            borderRadius: 6,
            border: "1px solid var(--hairline-strong)",
            background: "var(--surface)",
            color: "var(--ink-1)",
            outline: "none",
            fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              onClose();
            }
          }}
        />
      </div>

      <div style={{ maxHeight: 280, overflowY: "auto", padding: "4px 0" }}>
        {items.length === 0 ? (
          <div
            style={{
              padding: "16px 14px",
              fontSize: 12.5,
              color: "var(--ink-4)",
              textAlign: "center",
            }}
          >
            {debounced.length === 0 ? t.assignEmpty : t.assignNoResults}
          </div>
        ) : (
          items.map((u) => {
            const checked = valueSet.has(u.id);
            const needsInvite =
              (workspaceId !== undefined || boardId !== undefined) && !knownIds.has(u.id);
            const name = u.display_name ?? u.email;
            return (
              <button
                key={u.id}
                type="button"
                onClick={() => toggle(u.id)}
                aria-pressed={checked}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 12px",
                  background: checked ? "var(--surface-soft)" : "transparent",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                  fontSize: 13,
                  color: "var(--ink-1)",
                  fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = checked
                    ? "var(--surface-soft)"
                    : "transparent")
                }
              >
                <Avatar user={toAvatarUser(u)} size={26} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {name}
                    {u.username ? (
                      <span
                        style={{
                          marginLeft: 6,
                          color: "var(--ink-4)",
                          fontWeight: 400,
                          fontSize: 11.5,
                        }}
                      >
                        ({u.username.toUpperCase()})
                      </span>
                    ) : null}
                  </div>
                  <div
                    style={{
                      fontSize: 11.5,
                      color: needsInvite ? BRAND.orange : "var(--ink-3)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {needsInvite ? (inviteLabel ?? t.assignWillGuestAccess) : u.email}
                  </div>
                </div>
                {checked ? <Icon name="check" size={14} color="var(--accent)" /> : null}
              </button>
            );
          })
        )}
      </div>
    </>
  );
}
